import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { PhoneNumberUtil, PhoneNumberFormat } from "google-libphonenumber";
import { sign_in } from "../../api/calls/user/sign_in";
import { sign_up } from "../../api/calls/user/sign_up";
import { setDrawerOpen, setMainModal } from "./main";
import Cookie from "universal-cookie";
import { setBasket } from "./basket/basket";
const phoneUtil = PhoneNumberUtil.getInstance();
// E.164 input ("+61412345678") → { cell_country_code: "+61", cell_number: "0412345678" }
// Backend's NormalizePhoneNumber expects national format with leading 0 for AU/ZA; we
// reuse the same `formatNational` digits to stay consistent with what it'll persist.
function splitE164(phone) {
    try {
        const parsed = phoneUtil.parseAndKeepRawInput(phone);
        if (!phoneUtil.isValidNumber(parsed))
            return { cell_number: phone };
        const cell_country_code = `+${parsed.getCountryCode()}`;
        const national = phoneUtil.format(parsed, PhoneNumberFormat.NATIONAL).replace(/\D/g, "");
        return { cell_country_code, cell_number: national };
    }
    catch {
        return { cell_number: phone };
    }
}
export const REMEMBER_ME_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;
const isHttps = () => typeof window !== "undefined" && window.location.protocol === "https:";
// v2 sign-in embeds the user's details inside the access-token JWT instead of
// returning a separate `user` object, so we decode the payload to read them.
function decodeJwtPayload(token) {
    if (!token || typeof token !== "string")
        return null;
    try {
        return JSON.parse(atob(token.split(".")[1]));
    }
    catch {
        return null;
    }
}
// Strip large/sensitive fields before persisting the user slice in a cookie
// (browsers silently drop cookies over ~4 KB, and we don't want password/OTPs on disk).
export function buildUserStateCookieValue(state) {
    const u = state?.user ?? {};
    return {
        user: {
            _id: u._id,
            email: u.email,
            name: u.name,
            surname: u.surname,
            cell_number: u.cell_number,
            client_id: u.client_id,
            remember_me: u.remember_me,
        },
        address: state?.address,
        client_currency: state?.client_currency,
        sell_out_of_stock: state?.sell_out_of_stock,
    };
}
const _devMode = process.env.NODE_ENV !== "production";
const initialState = {
    user: {
        user: {
            _id: "",
            email: _devMode ? "fred@doshex.com" : "",
            password: _devMode ? "1234" : "",
            confirm_password: undefined,
            name: undefined,
            surname: undefined,
            reset_password_otp: undefined,
            client_id: undefined,
            remember_me: true,
        },
        token: "",
        is_loading: true,
        address: undefined,
        client_currency: "R",
        sell_out_of_stock: false,
    },
};
const sign_in_thunk = createAsyncThunk("user/sign_in_thunk", async (arg, { dispatch, getState }) => {
    const cookie = new Cookie();
    const state = getState();
    // console.log("sign_in_thunk STATE", state);
    const { user } = state.userState;
    const response = await sign_in({
        email: user.user.email,
        password: user.user.password,
    });
    // console.log("user/sign_in_thunk", response);
    if (response.success) {
        const signInResponse = response.content;
        // v2 sign-in returns: { user_token: { token, refresh_token }, client_token: { token, refresh_token } }
        const userAccessToken = signInResponse?.user_token?.token ?? "";
        const userRefreshToken = signInResponse?.user_token?.refresh_token ?? "";
        const clientAccessToken = signInResponse?.client_token?.token ?? "";
        const clientRefreshToken = signInResponse?.client_token?.refresh_token ?? "";
        // The user's details live inside the access-token JWT (no `user` object anymore).
        const decodedUser = decodeJwtPayload(userAccessToken);
        // Always remember the user, so all auth cookies persist for the full window.
        const cookieOptions = {
            path: "/",
            sameSite: "lax",
            secure: isHttps(),
            maxAge: REMEMBER_ME_MAX_AGE_SECONDS,
        };
        // Access token for user-specific calls; refresh tokens for renewing on load.
        cookie.set("user_token", userAccessToken, cookieOptions);
        cookie.set("user_refresh_token", userRefreshToken, cookieOptions);
        cookie.set("client_refresh_token", clientRefreshToken, cookieOptions);
        const obj = {
            ...user,
            is_loading: false,
            user: {
                ...user.user,
                _id: decodedUser?._id ?? user.user._id,
                client_id: decodedUser?.client_id ?? user.user.client_id,
                name: decodedUser?.name ?? user.user.name,
                surname: decodedUser?.surname ?? user.user.surname,
                email: decodedUser?.email ?? user.user.email,
                cell_number: decodedUser?.cell_number ?? user.user.cell_number,
            },
            // Most API calls authenticate with the client token.
            token: clientAccessToken,
        };
        cookie.set("user_state", buildUserStateCookieValue(obj), cookieOptions);
        dispatch(setUser(obj));
        dispatch(setDrawerOpen(false));
    }
    else {
        dispatch(setMainModal({ title: "Something went wrong!", messages: [response.message] }));
    }
    return response;
});
const sign_out_thunk = createAsyncThunk("user/sign_out_thunk", async (arg, { dispatch, getState }) => {
    const cookie = new Cookie();
    const state = getState();
    // Retrieve the current token.
    const { user } = state.userState;
    const currentToken = user.token || "";
    // Delete the user & token cookies.
    cookie.remove("user_state");
    cookie.remove("user_token");
    // Unset the state.
    dispatch(setUser({
        user: {
            _id: undefined,
            email: "",
            password: "",
            name: undefined,
            surname: undefined,
        },
        token: currentToken,
        is_loading: false,
    }));
    // Reset the basket state.
    dispatch(setBasket(null));
    //Close the drawer.
    dispatch(setDrawerOpen(false));
});
const update_details_thunk = createAsyncThunk("user/update_details_thunk", async (arg, { dispatch, getState }) => {
    const { name, surname, email, phoneNumber } = arg;
    // Delete the user cookie.
    const cookie = new Cookie();
    const state = getState();
    const { user } = state.userState;
    const newUserObj = {
        ...user,
        user: {
            ...user.user,
            name: name,
            surname: surname,
            email: email,
            cell_number: phoneNumber,
        },
    };
    // If cookie was saved before, update it.
    if (cookie.get("user_state")) {
        cookie.set("user_state", buildUserStateCookieValue(newUserObj), {
            path: "/",
            sameSite: "lax",
            secure: isHttps(),
            maxAge: REMEMBER_ME_MAX_AGE_SECONDS,
        });
    }
    // Update the state.
    dispatch(setUser(newUserObj));
});
// const update_password_thunk = createAsyncThunk(
//   "user/update_password_thunk",
//   async (arg: { newPassword: string; currentPassword: string }, { dispatch, getState }) => {
//     const { newPassword, currentPassword } = arg;
//     // Delete the user cookie.
//     const cookie = new Cookie();
//     update_password({
//       current_password: currentPassword,
//       new_password: newPassword,
//     }).then((response) => {
//       // setIsSaving(false);
//       if (response.success) {
//         // setIsEditing(false);
//         // Update the state.
//         dispatch(setUser(response.content));
//         // If cookie was saved before, update it.
//         if (cookie.get("user_state")) {
//           cookie.set("user_state", response.content, {
//             sameSite: true,
//           });
//         }
//       } else {
//         dispatch(setMainModal({ title: "Something went wrong!", messages: [response.message] }));
//       }
//     });
//   }
// );
const sign_up_thunk = createAsyncThunk("user/sign_up_thunk", async (arg, { dispatch, getState }) => {
    const cookie = new Cookie();
    const state = getState();
    const { user } = state.userState;
    const { cell_country_code, cell_number } = splitE164(user.user.cell_number ?? "");
    const response = await sign_up({
        client_id: process.env.LETS_TRADE_CLIENT_ID ?? "",
        name: user.user.name ?? "",
        surname: user.user.surname ?? "",
        email: user.user.email ?? "",
        cell_number,
        cell_country_code,
        password: user.user.password ?? "",
        get_communication: user.user.get_communication ?? false,
        agrees_terms_and_conditions: user.user.agrees_terms_and_conditions ?? false,
    });
    if (response.success) {
        const signUpResponse = response.content;
        // v2 sign-up returns: { user, user_token: { token, refresh_token }, client_token: { token, refresh_token } }
        const userAccessToken = signUpResponse?.user_token?.token ?? "";
        const userRefreshToken = signUpResponse?.user_token?.refresh_token ?? "";
        const clientAccessToken = signUpResponse?.client_token?.token ?? "";
        const clientRefreshToken = signUpResponse?.client_token?.refresh_token ?? "";
        const cookieOptions = {
            path: "/",
            sameSite: "lax",
            secure: isHttps(),
            maxAge: REMEMBER_ME_MAX_AGE_SECONDS,
        };
        cookie.set("user_token", userAccessToken, cookieOptions);
        cookie.set("user_refresh_token", userRefreshToken, cookieOptions);
        cookie.set("client_refresh_token", clientRefreshToken, cookieOptions);
        const newUser = signUpResponse?.user ?? {};
        const obj = {
            ...user,
            is_loading: false,
            user: {
                ...user.user,
                _id: newUser?._id ?? user.user._id,
                client_id: newUser?.client_id ?? user.user.client_id,
                name: newUser?.name ?? user.user.name,
                surname: newUser?.surname ?? user.user.surname,
                email: newUser?.email ?? user.user.email,
                cell_number: newUser?.cell_number ?? cell_number,
            },
            // Most API calls authenticate with the client token.
            token: clientAccessToken,
        };
        cookie.set("user_state", buildUserStateCookieValue(obj), cookieOptions);
        dispatch(setUser(obj));
        dispatch(setDrawerOpen(false));
        dispatch(setMainModal({
            title: `Welcome, ${user.user.name || "User"}!`,
            messages: ["You have been registered successfully."],
        }));
    }
    else {
        dispatch(setMainModal({ title: "Something went wrong!", messages: [response.message] }));
    }
    return response;
});
const slice = createSlice({
    name: "user",
    initialState,
    reducers: {
        setUser: (state, action) => {
            state.user = action.payload;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(sign_in_thunk.fulfilled, (state, action) => {
            // console.log("sign_in_thunk.fulfilled", action);
            state.user.is_loading = false;
        })
            .addCase(sign_in_thunk.rejected, (state, action) => {
            // console.log("sign_in_thunk.rejected", action);
            state.user.is_loading = false;
        })
            .addCase(sign_in_thunk.pending, (state, action) => {
            // console.log("sign_in_thunk.pending", action);
            state.user.is_loading = true;
        })
            .addCase(sign_up_thunk.fulfilled, (state, action) => {
            // console.log("sign_up_thunk.fulfilled", action);
            state.user.is_loading = false;
        })
            .addCase(sign_up_thunk.rejected, (state, action) => {
            // console.log("sign_up_thunk.rejected", action);
            state.user.is_loading = false;
        })
            .addCase(sign_up_thunk.pending, (state, action) => {
            // console.log("sign_up_thunk.pending", action);
            state.user.is_loading = true;
        });
        // .addCase(update_password_thunk.fulfilled, (state, action) => {
        //   // console.log("update_password_thunk.fulfilled", action);
        //   state.user.is_saving = false;
        // })
        // .addCase(update_password_thunk.rejected, (state, action) => {
        //   // console.log("update_password_thunk.rejected", action);
        //   state.user.is_saving = false;
        // })
        // .addCase(update_password_thunk.pending, (state, action) => {
        //   // console.log("update_password_thunk.pending", action);
        //   state.user.is_saving = true;
        // });
    },
});
export { sign_in_thunk, sign_out_thunk, update_details_thunk, 
//update_password_thunk,
sign_up_thunk, };
export const { setUser } = slice.actions;
export default slice.reducer;
